import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuthState, useSignInWithGoogle } from "react-firebase-hooks/auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import auth from "../firebase.init";
import Loading from "../components/Shared/Loading";


const getErrorMessage = (errorCode) => {
  switch (errorCode) {
    case "auth/popup-closed-by-user":
      return "Sign-in popup closed by user.";
    case "auth/popup-blocked":
      return "Sign-in popup was blocked. Please allow popups for this site.";
    case "auth/cancelled-popup-request":
      return "Sign-in cancelled. Please try again.";
    case "auth/network-request-failed":
      return "Network error. Please check your connection and try again.";
    case "auth/user-not-found":
      return "No account found with this email address.";
    case "auth/wrong-password":
      return "Incorrect password. Please try again.";
    case "auth/invalid-email":
      return "Invalid email address format.";
    case "auth/user-disabled":
      return "This account has been disabled.";
    case "auth/too-many-requests":
      return "Too many failed login attempts. Please try again later.";
    default:
      return "An error occurred during login. Please try again.";
  }
};

const Login = () => {
  const [signInWithGoogle, gUser, gLoading, gError] = useSignInWithGoogle(auth);
  const [apiError, setApiError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailPasswordError, setEmailPasswordError] = useState(null);
  const [emailPasswordLoading, setEmailPasswordLoading] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [userMail] = useAuthState(auth);

  useEffect(() => {
    if (userMail) {
      navigate("/user/dashboard", { replace: true });
    }
  }, [userMail, navigate]);

  let from = location.state?.from?.pathname || "/user/dashboard";
  let signUpError;

  useEffect(() => {
    if (gError || apiError || emailPasswordError) {
      window.scrollTo(0, 0);
    }
  }, [gError, apiError, emailPasswordError]);

  // Create user in database after successful Firebase authentication
  const createUserInDb = async (name, email, photoURL) => {
    try {
      const response = await fetch(
        `https://allinoneai-d1a5a1b54c64.herokuapp.com/api/create-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userName: name,
            userEmail: email,
            profileURL: photoURL || null,
          }),
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || "Failed to create user");
      }
      console.log("User created with credits:", data);
      return data;
    } catch (err) {
      console.error("Error creating user in database:", err);
      setApiError(err.message);
      return null;
    }
  };

  useEffect(() => {
    const handleUserCreation = async () => {
      if (gUser) {
        setIsProcessing(true);
        const { displayName, email, photoURL } = auth.currentUser;
        await createUserInDb(displayName, email, photoURL);
        setIsProcessing(false);
        navigate(from, { replace: true });
      }
    };
    handleUserCreation();
  }, [gUser, from, navigate]);

  const handleEmailPasswordLogin = async (e) => {
    e.preventDefault();
    setEmailPasswordLoading(true);
    setApiError(null);
    setEmailPasswordError(null);

    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      if (result.user) {
        setIsProcessing(true);
        const { displayName, email, photoURL } = result.user;
        await createUserInDb(displayName, email, photoURL);
        setIsProcessing(false);
        navigate(from, { replace: true });
      }
    } catch (error) {
      setEmailPasswordError(getErrorMessage(error.code));
      console.error("Email/password login error:", error);
    } finally {
      setEmailPasswordLoading(false);
    }
  };

  if (gLoading || isProcessing || emailPasswordLoading) {
    return <Loading />;
  }

  if (gError || apiError || emailPasswordError) {
    const errorCode = gError?.code;
    signUpError = (
      <div className="error-message">
        <i className="fas fa-exclamation-circle"></i>
        <p>{apiError || emailPasswordError || getErrorMessage(errorCode)}</p>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Login - Welcome Back</title>
        <meta
          name="description"
          content="Discover proven strategies and tools to instantly find high-quality SEO clients. Learn how to attract, pitch, and close deals faster to grow your digital marketing business."
        />
      </Helmet>
      <section
        className="modern-register-page body-bg"
        style={{ marginTop: "0px" }}
      >
        <div className="register-container">
          <div className="register-card">
            <div className="register-header">
              <div className="close-button">
                <Link to="/" className="close-link">
                  <i className="fas fa-times"></i>
                </Link>
              </div>
              <div className="logo-section"></div>
              <div className="welcome-section ">
                <h1>Welcome Back</h1>
                <p>Sign in to continue to your account</p>
              </div>
            </div>
            <div className="register-content">
              {signUpError}

              {/* Email/Password Form */}
              <form
                onSubmit={handleEmailPasswordLogin}
                className="email-password-form"
              >
                <div className="form-group">
                  <label htmlFor="email">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="Enter your email"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password</label>
                  <input
                    type="password"
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="Enter your password"
                  />
                </div>
                <div className="form-options">
                  <Link to="/reset-password" className="reset-password">
                    Forgot Password?
                  </Link>
                </div>
                <button
                  type="submit"
                  className="email-password-signin-btn"
                  disabled={emailPasswordLoading}
                >
                  {emailPasswordLoading ? "Signing In..." : "Sign In"}
                </button>
              </form>

              <div className="features-section">
                <h3>Your Account Benefits?</h3>
                <div className="features-grid">
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Personalized dashboard</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Save your Preferences</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>Track your progress</span>
                  </div>
                  <div className="feature-item">
                    <i className="fas fa-check-circle"></i>
                    <span>24/7 Support</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="register-footer">
              <p>
                Don't have an account?{" "}
                <Link to="/register" state={location.state} className="register-link">
                  Sign up for free
                </Link>
              </p>
              {/* <div className="terms-section">
                <p>By continuing, you agree to our <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a></p>
              </div> */}
            </div>
          </div>
          
        </div>
        
        <style jsx>{`
          .modern-register-page {
            min-height: 100vh;
            background-color: var(--bg-dark-primary);
            background-image: radial-gradient(circle at top right, rgba(99, 102, 241, 0.15), transparent 40%), radial-gradient(circle at bottom left, rgba(236, 72, 153, 0.1), transparent 40%);
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 100px 20px 60px;
            position: relative;
            overflow-y: auto;
            overflow-x: hidden;
          }
          .register-container {
            position: relative;
            z-index: 1;
            width: 100%;
            max-width: 500px;
            margin: auto;
          }
          .register-card {
            background: var(--bg-dark-secondary);
            backdrop-filter: blur(16px);
            border-radius: var(--border-radius-xl);
            box-shadow: var(--shadow-lg);
            overflow: hidden;
            border: 1px solid var(--glass-border);
          }
          .register-header {
            padding: 40px 40px 30px;
            text-align: center;
            position: relative;
          }
          .close-button {
            position: absolute;
            top: 20px;
            right: 20px;
          }
          .close-link {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 40px;
            height: 40px;
            background: var(--bg-dark-tertiary);
            border: 1px solid var(--glass-border);
            border-radius: 50%;
            color: var(--text-secondary);
            text-decoration: none;
            transition: all 0.3s ease;
          }
          .close-link:hover {
            background: rgba(255, 255, 255, 0.05);
            color: var(--text-primary);
            transform: scale(1.05);
          }
          .logo-section {
            margin-bottom: 30px;
          }
          .logo {
            height: 60px;
            width: auto;
          }
          .welcome-section h1 {
            font-size: 28px;
            font-weight: 700;
            color: var(--text-primary);
            margin-bottom: 10px;
          }
          .welcome-section p {
            font-size: 16px;
            color: var(--text-secondary);
            margin: 0;
          }
          .register-content {
            padding: 0 40px 30px;
          }
          .error-message {
            background: rgba(239, 68, 68, 0.1);
            border: 1px solid rgba(239, 68, 68, 0.2);
            border-radius: 12px;
            padding: 15px;
            margin-bottom: 25px;
            display: flex;
            align-items: center;
            gap: 10px;
          }
          .error-message i {
            color: var(--danger);
            font-size: 18px;
          }
          .error-message p {
            color: var(--danger);
            margin: 0;
            font-size: 14px;
          }

          /* Email/Password Form Styles */
          .email-password-form {
            margin-bottom: 25px;
          }
          .form-group {
            margin-bottom: 20px;
            text-align: left;
          }
          .form-group label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--text-primary);
            font-size: 14px;
          }
          .form-group input {
            width: 100%;
            padding: 14px 16px;
            border: 1px solid var(--glass-border);
            border-radius: 10px;
            font-size: 15px;
            transition: all 0.3s ease;
            background-color: var(--bg-dark-tertiary);
            color: var(--text-primary);
          }
          .form-group input::placeholder {
            color: var(--text-muted);
          }
          .form-group input:focus {
            outline: none;
            border-color: var(--accent-primary);
            box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.25);
            background-color: var(--bg-dark-secondary);
          }
          .form-options {
            display: flex;
            justify-content: flex-end;
            align-items: center;
            margin-bottom: 20px;
          }
          .remember-me {
            display: flex;
            align-items: center;
            gap: 8px;
          }
          .remember-me input {
            width: 16px;
            height: 16px;
            accent-color: var(--accent-primary);
          }
          .remember-me label {
            font-size: 14px;
            color: var(--text-secondary);
            cursor: pointer;
          }
          .reset-password {
            font-size: 14px;
            color: var(--accent-primary);
            text-decoration: none;
            font-weight: 500;
          }
          .reset-password:hover {
            text-decoration: underline;
            color: var(--accent-primary-hover);
          }
          .email-password-signin-btn {
            width: 100%;
            height: 56px;
            background: var(--accent-primary);
            border: none;
            border-radius: 12px;

            font-size: 16px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            box-shadow: var(--shadow-glow);
          }
          .email-password-signin-btn:hover {
            background: var(--accent-primary-hover);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
          }
          .email-password-signin-btn:disabled {
            background: var(--bg-dark-tertiary);
            color: var(--text-muted);
            cursor: not-allowed;
            transform: none;
            box-shadow: none;
          }

          .divider {
            display: flex;
            align-items: center;
            margin: 25px 0;
            color: var(--text-muted);
            font-size: 14px;
          }
          .divider::before,
          .divider::after {
            content: "";
            flex: 1;
            height: 1px;
            background: var(--glass-border);
          }
          .divider span {
            padding: 0 15px;
          }

          .google-signin-section {
            margin-bottom: 30px;
          }
          .google-signin-btn {
            width: 100%;
            height: 56px;
            background: var(--bg-dark-tertiary);
            border: 1px solid var(--glass-border);
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 12px;
            font-size: 16px;
            font-weight: 500;
            color: var(--text-primary);
            cursor: pointer;
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
          }
          .google-signin-btn:hover {
            border-color: var(--accent-primary);
            box-shadow: 0 4px 12px rgba(99, 102, 241, 0.2);
            transform: translateY(-2px);
          }
          .google-signin-btn:active {
            transform: translateY(0);
          }
          .google-signin-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none;
          }
          .google-icon {
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .features-section {
            text-align: center;
            margin-top: 30px;
          }
          .features-section h3 {
            font-size: 18px;
            font-weight: 600;
            color: var(--text-primary);
            margin-bottom: 20px;
          }
          .features-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15px;
          }
          .feature-item {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px;
            background: rgba(99, 102, 241, 0.1);
            border: 1px solid rgba(99, 102, 241, 0.15);
            border-radius: 10px;
            font-size: 14px;
            color: var(--text-secondary);
            transition: all 0.2s;
          }
          .feature-item:hover {
            background: rgba(99, 102, 241, 0.15);
            transform: translateY(-2px);
          }
          .feature-item i {
            color: var(--accent-primary);
            font-size: 16px;
          }
          .register-footer {
            padding: 30px 40px;
            background: rgba(0, 0, 0, 0.2);
            text-align: center;
            border-top: 1px solid var(--glass-border);
          }
          .register-footer p {
            margin: 0 0 15px;
            color: var(--text-secondary);
            font-size: 14px;
          }
          .register-link {
            color: var(--accent-primary);
            text-decoration: none;
            font-weight: 500;
          }
          .register-link:hover {
            text-decoration: underline;
            color: var(--accent-primary-hover);
          }
          .terms-section p {
            font-size: 12px;
            color: var(--text-muted);
            margin: 0;
          }
          .terms-section a {
            color: var(--accent-primary);
            text-decoration: none;
          }
          .terms-section a:hover {
            text-decoration: underline;
          }
          @media (max-width: 768px) {
            .modern-register-page {
              padding: 120px 15px 40px 15px;
              align-items: flex-start; /* Prevent top cutoff on small screens */
            }
            .register-container {
              margin: 0 auto;
            }
            .register-header {
              padding: 30px 25px 20px;
            }
            .register-content {
              padding: 0 25px 20px;
            }
            .register-footer {
              padding: 20px 25px;
            }
            .welcome-section h1 {
              font-size: 24px;
            }
            .close-button {
              top: 15px;
              right: 15px;
            }
            .close-link {
              width: 32px;
              height: 32px;
              font-size: 14px;
            }
            .features-grid {
              grid-template-columns: 1fr;
            }
            .feature-item {
              justify-content: flex-start; /* Keep text aligned properly */
            }
          }
          @media (max-width: 480px) {
            .register-header {
              padding: 25px 20px 15px;
            }
            .register-content {
              padding: 0 20px 15px;
            }
            .register-footer {
              padding: 15px 20px;
            }
            .welcome-section h1 {
              font-size: 22px;
            }
            .welcome-section p {
              font-size: 14px;
            }
            .google-signin-btn {
              height: 50px;
              font-size: 14px;
            }
            .email-password-signin-btn {
              height: 50px;
              font-size: 15px;
            }
          }
        `}</style>
      </section>
    </>
  );
};

export default Login;
